import {
  ComputerAgentDataStatus,
  MessageRole,
  StreamEventType,
  ToolCallStatus,
  ToolName,
} from "../types";
import { ComputerAgentResults, NavigationItem } from "../stores/types";
import { SessionStore } from "../stores/session-store";
import { MessageStore } from "../stores/message-store";
import { StreamStore } from "../stores/stream-store";

import { parseToolCallsFromJson } from "./parsers";
import {
  findTargetMessageIndex,
  hasEmptyAssistantMessage,
  shouldCompleteToolCallsAfterEvent,
} from "./messageHelpers";

export interface EventHandlerArgs {
  chatId: string;
  data: Record<string, unknown>;
  sessions: SessionStore;
  messages: MessageStore;
  stream: StreamStore;
  onStreamError?: (errorMessage: string) => void;
}

type EventHandler = (args: EventHandlerArgs) => void;

const resolveDeltaContentState = ({
  chatId,
  content,
  messages,
}: {
  chatId: string;
  content: string;
  messages: MessageStore;
}): {
  messageRole: MessageRole.ASSISTANT | MessageRole.MASTER_VIBE_PROMPT;
  processedContent: string;
  shouldCreateNewBubble: boolean;
} => {
  if (content.includes("[computer-agent]")) {
    messages.setCurrentMessageRole(chatId, MessageRole.ASSISTANT);
    return {
      messageRole: MessageRole.ASSISTANT,
      processedContent: content.replace(/\[computer-agent\]\s*/g, ""),
      shouldCreateNewBubble: false,
    };
  }

  if (content.includes("[master-vibe-prompt]")) {
    messages.setCurrentMessageRole(chatId, MessageRole.MASTER_VIBE_PROMPT);
    return {
      messageRole: MessageRole.MASTER_VIBE_PROMPT,
      processedContent: content.replace(/\[master-vibe-prompt\]\s*/g, ""),
      shouldCreateNewBubble: true,
    };
  }

  if (messages.getIsFirstDelta(chatId)) {
    messages.setCurrentMessageRole(chatId, MessageRole.ASSISTANT);
    return {
      messageRole: MessageRole.ASSISTANT,
      processedContent: content,
      shouldCreateNewBubble: false,
    };
  }

  return {
    messageRole: messages.getCurrentMessageRole(chatId),
    processedContent: content,
    shouldCreateNewBubble: false,
  };
};

const handleDelta = ({ chatId, data, messages }: EventHandlerArgs) => {
  const content = data.content as string;
  if (!content) {
    return;
  }

  const {
    messageRole,
    shouldCreateNewBubble,
    processedContent: resolvedContent,
  } = resolveDeltaContentState({ chatId, content, messages });

  const processedContent = resolvedContent.replace(/^Executing task:\s*/i, "");

  if (processedContent === null || processedContent === undefined) {
    return;
  }

  const wasFirstDelta = messages.getIsFirstDelta(chatId);
  if (messages.getIsFirstDelta(chatId)) {
    messages.setIsFirstDelta(chatId, false);
  }

  const assistantIndex = messages.getAssistantMessageIndex(chatId);
  const chatMessages = messages.getMessages(chatId);

  if (wasFirstDelta && hasEmptyAssistantMessage(chatMessages, assistantIndex)) {
    messages.set(chatId, assistantIndex, {
      role: messageRole,
      content: processedContent,
    });
    return;
  }

  if (shouldCreateNewBubble) {
    if (messageRole === MessageRole.MASTER_VIBE_PROMPT) {
      const lastMasterVibeIdx = chatMessages.findLastIndex(
        m =>
          m.role === MessageRole.MASTER_VIBE_PROMPT &&
          m.content.trim().length > 0 &&
          m.content.toLowerCase().includes("executing task"),
      );

      if (lastMasterVibeIdx !== -1) {
        messages.set(chatId, lastMasterVibeIdx, {
          role: messageRole,
          content: processedContent,
        });
        messages.setAssistantMessageIndex(chatId, lastMasterVibeIdx);
        return;
      }
    }

    if (hasEmptyAssistantMessage(chatMessages, assistantIndex)) {
      messages.set(chatId, assistantIndex, {
        role: messageRole,
        content: processedContent,
      });
      return;
    }
    const targetIndex = chatMessages.length;
    messages.push(chatId, { role: messageRole, content: processedContent });
    messages.setAssistantMessageIndex(chatId, targetIndex);
    return;
  }

  const targetIndex = findTargetMessageIndex(chatMessages, messageRole, assistantIndex);
  if (targetIndex === -1) {
    const newIndex = chatMessages.length;
    messages.push(chatId, { role: messageRole, content: processedContent });
    messages.setAssistantMessageIndex(chatId, newIndex);
    if (messageRole === MessageRole.MASTER_VIBE_PROMPT) {
      messages.setAssistantMessageIndex(chatId, newIndex);
    }
  } else {
    messages.updateContent(chatId, targetIndex, currentContent => {
      const newContent = currentContent + processedContent;

      try {
        const parsed = JSON.parse(newContent);
        if (parsed && Array.isArray(parsed.tool_calls)) {
          for (const toolCall of parsed.tool_calls) {
            const toolName = toolCall.tool_name || "unknown";
            const toolInput = toolCall.tool_input || {};
            const newToolCallId = `tool-${Date.now()}-${Math.random()}`;

            messages.push(chatId, {
              role: MessageRole.TOOL_CALL,
              content: "",
              toolCall: {
                id: newToolCallId,
                name: toolName,
                input: toolInput,
                status: ToolCallStatus.COMPLETED,
                completedAt: Date.now(),
              },
            });
          }
          return "";
        }
      } catch {
        // nothing
      }

      return newContent.replace(/^Executing task:\s*/i, "");
    });
    if (messageRole === MessageRole.MASTER_VIBE_PROMPT) {
      messages.setAssistantMessageIndex(chatId, targetIndex);
    }
  }
};

const handleToolCall = ({ chatId, data, messages }: EventHandlerArgs) => {
  const toolName = (data.tool_name as string) || "unknown";
  let toolInput: Record<string, unknown> = {};
  const rawToolInput = data.tool_input;

  if (typeof rawToolInput === "string") {
    try {
      toolInput = JSON.parse(rawToolInput);
    } catch {
      toolInput = { raw: rawToolInput };
    }
  } else if (rawToolInput && typeof rawToolInput === "object") {
    toolInput = rawToolInput as Record<string, unknown>;
  }

  const hasExplicitId = Boolean(data.tool_call_id);
  const toolCallId = (data.tool_call_id as string) || `tool-${Date.now()}`;

  let navigation: NavigationItem[] | undefined;
  if (toolName === ToolName.RETURN_RESULTS && toolInput.results) {
    const results = toolInput.results as Record<string, unknown>;
    if (results.navigation && Array.isArray(results.navigation)) {
      navigation = results.navigation as NavigationItem[];
    }
  }

  const chatMessages = messages.getMessages(chatId);

  let existingIdx = -1;
  if (hasExplicitId) {
    existingIdx = chatMessages.findIndex(
      m => m.role === MessageRole.TOOL_CALL && m.toolCall?.id === toolCallId,
    );
  }
  if (existingIdx === -1 && !hasExplicitId) {
    existingIdx = chatMessages.findLastIndex(
      m =>
        m.role === MessageRole.TOOL_CALL &&
        m.toolCall?.name === toolName &&
        m.toolCall?.status === ToolCallStatus.RUNNING,
    );
  }

  if (existingIdx !== -1) {
    messages.set(chatId, existingIdx, {
      ...chatMessages[existingIdx],
      toolCall: {
        ...chatMessages[existingIdx].toolCall!,
        input: toolInput,
        navigation,
      },
    });
  } else {
    messages.completeRunningToolCalls(chatId);

    const isReturnResults = toolName === ToolName.RETURN_RESULTS;

    messages.push(chatId, {
      role: MessageRole.TOOL_CALL,
      content: "",
      toolCall: {
        id: toolCallId,
        name: toolName,
        input: toolInput,
        status: isReturnResults ? ToolCallStatus.COMPLETED : ToolCallStatus.RUNNING,
        startedAt: Date.now(),
        completedAt: isReturnResults ? Date.now() : undefined,
        navigation,
      },
    });

    // return_results is a terminal/hidden tool — keep appending follow-up text to the
    // existing assistant bubble instead of creating an orphan second reply.
    if (!isReturnResults) {
      messages.setAssistantMessageIndex(chatId, -1);
    }
  }
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const parseJsonRecord = (value: unknown): Record<string, unknown> | null => {
  if (typeof value !== "string") {
    return asRecord(value);
  }

  try {
    return asRecord(JSON.parse(value));
  } catch {
    return null;
  }
};

const getToolResultError = (
  result: Record<string, unknown> | null,
  data: Record<string, unknown>,
) => {
  const error = result?.error ?? data.error ?? data.message;
  return typeof error === "string" ? error : undefined;
};

const handleToolResult = ({ chatId, data, messages }: EventHandlerArgs) => {
  const toolCallId = (data.tool_call_id || data.id || data.call_id) as string;
  const output = data.output ?? data.result ?? data.response;
  const result = asRecord(data.result) ?? parseJsonRecord(output);
  const isFailure = result?.success === false || data.success === false;
  const error = getToolResultError(result, data);
  let navigation = Array.isArray(data.navigation)
    ? (data.navigation as NavigationItem[])
    : undefined;

  if (result?.success === true && result.is_done === true) {
    return;
  }
  if (!navigation && result && Array.isArray(result.navigation)) {
    navigation = result.navigation as NavigationItem[];
  }
  if (!navigation && result && result.results && typeof result.results === "object") {
    const results = result.results as Record<string, unknown>;
    if (Array.isArray(results.navigation)) {
      navigation = results.navigation as NavigationItem[];
    }
  }

  const chatMessages = messages.getMessages(chatId);

  const parsedToolCalls = parseToolCallsFromJson(typeof output === "string" ? output : "");

  if (parsedToolCalls && parsedToolCalls.length > 0) {
    for (const toolCall of parsedToolCalls) {
      const toolName = toolCall.tool_name || "unknown";
      const toolInput = toolCall.tool_input || {};
      const newToolCallId = `tool-${Date.now()}-${Math.random()}`;

      messages.push(chatId, {
        role: MessageRole.TOOL_CALL,
        content: "",
        toolCall: {
          id: newToolCallId,
          name: toolName,
          input: toolInput,
          status: ToolCallStatus.COMPLETED,
          completedAt: Date.now(),
        },
      });
    }
    return;
  }

  const idxToUpdate = messages.findToolCallToUpdate(chatId, toolCallId);

  if (idxToUpdate !== -1) {
    const msg = chatMessages[idxToUpdate];
    messages.set(chatId, idxToUpdate, {
      ...msg,
      toolCall: {
        ...msg.toolCall!,
        output,
        navigation: navigation ?? msg.toolCall!.navigation,
        error,
        status: isFailure ? ToolCallStatus.ERROR : ToolCallStatus.COMPLETED,
        completedAt: Date.now(),
      },
    });
  }
};

const handleToolError = ({ chatId, data, messages }: EventHandlerArgs) => {
  const toolCallId = data.tool_call_id as string;
  const error = (data.error as string) || (data.message as string) || "Unknown error";

  const idxToUpdate = messages.findToolCallToUpdate(chatId, toolCallId);

  if (idxToUpdate !== -1) {
    const chatMessages = messages.getMessages(chatId);
    const msg = chatMessages[idxToUpdate];
    messages.set(chatId, idxToUpdate, {
      ...msg,
      toolCall: {
        ...msg.toolCall!,
        error,
        status: ToolCallStatus.ERROR,
        completedAt: Date.now(),
      },
    });
  }
};

const handleDone = ({ chatId, messages, stream }: EventHandlerArgs) => {
  messages.completeRunningToolCalls(chatId);
  messages.completeRunningComputerAgents(chatId);
  messages.removeTrailingEmptyAssistants(chatId);
  stream.triggerStreamComplete(chatId);
  // Keep isLoading true until the SSE reader finishes so queue drain / new sends
  // cannot start a concurrent request while this stream is still open.
  messages.setCurrentMessageRole(chatId, MessageRole.ASSISTANT);
};

const handleError = ({ data, onStreamError }: EventHandlerArgs) => {
  const errorObj = data.error as { message?: string } | undefined;
  const errorMessage = (data.message as string) || errorObj?.message || "Streaming error";
  if (onStreamError) {
    onStreamError(errorMessage);
  }
};

const handleSessionCreated = ({ chatId, data, sessions, messages }: EventHandlerArgs) => {
  const sessionId = data.session_id as string;
  if (!sessionId) {
    return;
  }

  if (sessionId === chatId) {
    return;
  }

  messages.transferMessages(chatId, sessionId);
  sessions.renameSessionId(chatId, sessionId);
};

const handleSessionNameGenerationStarted = ({
  chatId,
  data,
  sessions,
  messages,
}: EventHandlerArgs) => {
  const sessionId = data.session_id as string;
  if (!sessionId) {
    return;
  }

  if (sessionId !== chatId) {
    messages.transferMessages(chatId, sessionId);
    sessions.renameSessionId(chatId, sessionId);
  }

  sessions.setNameGenerating(sessionId, true);
};

const handleSessionNameDelta = ({ data, sessions }: EventHandlerArgs) => {
  const sessionId = data.session_id as string;
  const accumulated = data.accumulated as string;
  if (sessionId && accumulated) {
    sessions.updateNamePreview(sessionId, accumulated);
  }
};

const handleSessionNameGenerated = ({ data, sessions }: EventHandlerArgs) => {
  const sessionId = data.session_id as string;
  const name = data.name as string;
  if (sessionId && name) {
    sessions.updateName(sessionId, name);
  }
};

const handleSessionNameGenerationError = ({ data, sessions }: EventHandlerArgs) => {
  const sessionId = data.session_id as string;
  if (sessionId) {
    sessions.setNameGenerating(sessionId, false);
  }
};

const handleComputerAgentStart = ({ chatId, data, messages }: EventHandlerArgs) => {
  const progressPhrase = (data.content as string) || "Starting computer agent...";

  messages.push(chatId, {
    role: MessageRole.COMPUTER_AGENT,
    content: "",
    computerAgent: {
      status: ComputerAgentDataStatus.RUNNING,
      progressPhrase,
      startedAt: Date.now(),
    },
  });

  messages.setAssistantMessageIndex(chatId, -1);
};

const handleComputerAgentProgress = ({ chatId, data, messages }: EventHandlerArgs) => {
  const progressPhrase = (data.content as string) || "Processing...";
  const humanReadableParagraph = data.human_readable_paragraph as string | undefined;
  const messageIndex = messages.findComputerAgentMessageIndex(chatId);

  if (messageIndex === -1) {
    messages.push(chatId, {
      role: MessageRole.COMPUTER_AGENT,
      content: "",
      computerAgent: {
        status: ComputerAgentDataStatus.RUNNING,
        progressPhrase,
        humanReadableParagraph,
        startedAt: Date.now(),
      },
    });

    messages.setAssistantMessageIndex(chatId, -1);
    return;
  }

  messages.updateAt(chatId, messageIndex, currentMessage => {
    const { computerAgent } = currentMessage;
    if (!computerAgent) {
      return currentMessage;
    }

    return {
      ...currentMessage,
      computerAgent: {
        ...computerAgent,
        progressPhrase,
        humanReadableParagraph,
      },
    };
  });
};

const handleComputerAgentComplete = ({ chatId, data, messages }: EventHandlerArgs) => {
  const messageIndex = messages.findComputerAgentMessageIndex(chatId);
  const results = data.results as ComputerAgentResults | undefined;
  const humanReadableParagraph =
    typeof results?.human_readable_paragraph === "string"
      ? results.human_readable_paragraph
      : undefined;

  if (messageIndex === -1) {
    messages.push(chatId, {
      role: MessageRole.COMPUTER_AGENT,
      content: "",
      computerAgent: {
        status: ComputerAgentDataStatus.COMPLETED,
        humanReadableParagraph,
        ...(results && Object.keys(results).length > 0 ? { results } : {}),
      },
    });
    return;
  }

  const chatMessages = messages.getMessages(chatId);
  const currentMessage = chatMessages[messageIndex];
  if (currentMessage.computerAgent) {
    messages.set(chatId, messageIndex, {
      ...currentMessage,
      computerAgent: {
        ...currentMessage.computerAgent,
        status: ComputerAgentDataStatus.COMPLETED,
        humanReadableParagraph,
        ...(results && Object.keys(results).length > 0 ? { results } : {}),
      },
    });
  }

  messages.setCurrentMessageRole(chatId, MessageRole.ASSISTANT);
  messages.setAssistantMessageIndex(chatId, -1);
};

const handleComputerAgentError = ({ chatId, data, messages }: EventHandlerArgs) => {
  const errorMessage = (data.message as string) || "Computer agent error";
  const messageIndex = messages.findComputerAgentMessageIndex(chatId);

  if (messageIndex === -1) {
    messages.push(chatId, {
      role: MessageRole.COMPUTER_AGENT,
      content: "",
      computerAgent: {
        status: ComputerAgentDataStatus.ERROR,
        error: errorMessage,
      },
    });
    return;
  }

  const chatMessages = messages.getMessages(chatId);
  const currentMessage = chatMessages[messageIndex];
  if (currentMessage.computerAgent) {
    messages.set(chatId, messageIndex, {
      ...currentMessage,
      computerAgent: {
        ...currentMessage.computerAgent,
        status: ComputerAgentDataStatus.ERROR,
        error: errorMessage,
      },
    });
  }

  messages.setCurrentMessageRole(chatId, MessageRole.ASSISTANT);
  messages.setAssistantMessageIndex(chatId, -1);
};

const handlers: Partial<Record<StreamEventType, EventHandler>> = {
  [StreamEventType.DELTA]: handleDelta,
  [StreamEventType.TOOL_CALL]: handleToolCall,
  [StreamEventType.TOOL_RESULT]: handleToolResult,
  [StreamEventType.TOOL_OUTPUT]: handleToolResult,
  [StreamEventType.TOOL_ERROR]: handleToolError,
  [StreamEventType.DONE]: handleDone,
  [StreamEventType.ERROR]: handleError,
  [StreamEventType.SESSION_CREATED]: handleSessionCreated,
  [StreamEventType.SESSION_NAME_GENERATION_STARTED]: handleSessionNameGenerationStarted,
  [StreamEventType.SESSION_NAME_DELTA]: handleSessionNameDelta,
  [StreamEventType.SESSION_NAME_GENERATED]: handleSessionNameGenerated,
  [StreamEventType.SESSION_NAME_GENERATION_ERROR]: handleSessionNameGenerationError,
  [StreamEventType.COMPUTER_AGENT_START]: handleComputerAgentStart,
  [StreamEventType.COMPUTER_AGENT_PROGRESS]: handleComputerAgentProgress,
  [StreamEventType.COMPUTER_AGENT_COMPLETE]: handleComputerAgentComplete,
  [StreamEventType.COMPUTER_AGENT_ERROR]: handleComputerAgentError,
};

export interface StoreContext {
  chatId: string;
  sessions: SessionStore;
  messages: MessageStore;
  stream: StreamStore;
  onStreamError?: (errorMessage: string) => void;
}

export const dispatchEvent = (
  type: StreamEventType,
  data: Record<string, unknown>,
  stores: StoreContext,
) => {
  const eventType = type as StreamEventType;
  const { chatId, sessions, messages, stream, onStreamError } = stores;

  if (
    eventType !== StreamEventType.DONE &&
    eventType !== StreamEventType.ERROR &&
    !stream.hasStreamingStartedFor(chatId)
  ) {
    stream.markStreamingStarted(chatId);
  }

  if (eventType !== StreamEventType.DONE && eventType !== StreamEventType.ERROR) {
    stream.markReceivingEvents(chatId);
  }

  const handler = handlers[eventType];
  if (handler) {
    handler({ chatId, data, sessions, messages, stream, onStreamError });
    if (shouldCompleteToolCallsAfterEvent(eventType)) {
      messages.completeRunningToolCalls(chatId);
    }
    if (
      eventType === StreamEventType.TOOL_RESULT ||
      eventType === StreamEventType.TOOL_OUTPUT ||
      eventType === StreamEventType.TOOL_ERROR
    ) {
      stream.markToolCallSettled(chatId);
    }
  }
};
